import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle, Info } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface EditRecentPatternDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  botId: string;
  botName: string;
  currentPattern: string;
  currentMode: 'GATE' | 'OPEN';
  onSuccess: () => void;
}

export const EditRecentPatternDialog: React.FC<EditRecentPatternDialogProps> = ({
  open,
  onOpenChange,
  botId,
  botName,
  currentPattern,
  currentMode,
  onSuccess,
}) => {
  const [pattern, setPattern] = useState(currentPattern);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    setPattern(currentPattern);
    setValidationError(null);
  }, [currentPattern, open]);

  const validatePattern = (value: string): boolean => {
    if (value.length === 0) {
      setValidationError(null);
      return true;
    }
    
    if (value.length > 6) {
      setValidationError('Pattern cannot exceed 6 characters');
      return false;
    }
    
    if (!/^[01]+$/.test(value)) {
      setValidationError('Pattern must only contain 0 (LOSS) or 1 (WIN)');
      return false;
    }
    
    setValidationError(null);
    return true;
  };

  const handlePatternChange = (value: string) => {
    setPattern(value);
    validatePattern(value);
  };

  const handleSubmit = async () => {
    if (!validatePattern(pattern)) return;
    
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('re-evaluate-type2-pattern', {
        body: {
          bot_id: botId,
          recent_results: pattern,
        },
      });

      if (error) throw error;

      const result = data;
      
      toast.success(
        <div className="space-y-1">
          <div className="font-semibold">Pattern Updated Successfully</div>
          <div className="text-sm">
            Bot: <strong>{botName}</strong>
          </div>
          <div className="text-sm">
            New Mode: <Badge variant={result.new_mode === 'OPEN' ? 'default' : 'secondary'}>{result.new_mode}</Badge>
          </div>
          {result.reason && (
            <div className="text-xs text-muted-foreground">{result.reason}</div>
          )}
        </div>
      );

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Failed to update pattern:', error);
      toast.error(`Failed to update pattern: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPatternPreview = () => {
    if (!pattern) return null;
    
    return (
      <div className="font-mono text-sm space-x-1">
        {pattern.split('').map((char, idx) => (
          <span
            key={idx}
            className={char === '1' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
          >
            {char === '1' ? '✓' : '✗'}
          </span>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Recent Pattern</DialogTitle>
          <DialogDescription>
            Update the recent results pattern for <strong>{botName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current State */}
          <div className="p-3 bg-muted rounded-lg space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Current Mode:</span>
              <Badge variant={currentMode === 'OPEN' ? 'default' : 'secondary'}>
                {currentMode}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Current Pattern:</span>
              <span className="font-mono">{currentPattern || '(empty)'}</span>
            </div>
          </div>

          {/* Pattern Input */}
          <div className="space-y-2">
            <Label htmlFor="pattern">
              New Pattern <span className="text-muted-foreground text-xs">(0=LOSS, 1=WIN, max 6)</span>
            </Label>
            <Input
              id="pattern"
              value={pattern}
              onChange={(e) => handlePatternChange(e.target.value)}
              placeholder="e.g., 1011"
              maxLength={6}
              className={validationError ? 'border-destructive' : ''}
            />
            {validationError && (
              <p className="text-xs text-destructive">{validationError}</p>
            )}
          </div>

          {/* Pattern Preview */}
          {pattern && !validationError && (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Preview:</Label>
              {renderPatternPreview()}
              <p className="text-xs text-muted-foreground">
                {pattern.split('').filter(c => c === '1').length} wins, {pattern.split('').filter(c => c === '0').length} losses
              </p>
            </div>
          )}

          {/* Info Alert */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs space-y-1">
              <p>• Enter a binary string (0 for LOSS, 1 for WIN)</p>
              <p>• Maximum 6 characters (sliding window)</p>
              <p>• System will re-evaluate patterns and update bot mode automatically</p>
              <p>• Empty pattern = fresh start</p>
            </AlertDescription>
          </Alert>

          {/* Warning for mode change */}
          {currentMode === 'OPEN' && pattern !== currentPattern && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <strong>Warning:</strong> Changing the pattern may trigger a mode change and close the gate if a lock pattern matches.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !!validationError}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save & Re-evaluate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
